import { Config } from '@shared/configs/config';
import { EntryType } from '@shared/modules/entry/enums/entry-type.enum';
import { EConfiguratorFileGroup, EProjectScope, IConfiguratorAnswers } from '../models/configurator.model';
import { EXTERIOR_WORK_NONE } from './configurator-options.data';
import { hasBathroomWork, hasKitchen, isCompleteRenovation, isScope } from './configurator-predicates';

const T = 'ProjectConfiguratorV2.Files';

export interface IConfiguratorFileGroupConfig {
  key: EConfiguratorFileGroup;
  /** Translation keys under `ProjectConfiguratorV2.Files.groups.<key>`: title, tip. */
  title: string;
  tip: string;
  /** `accept` of the file input. */
  accept: string;
  /** On touch devices: "Kamera" and "Galerie" instead of one button. */
  camera: boolean;
  /** Offers "Habe ich nicht". */
  skippable: boolean;
  /** Entry types the files are stored in; the first one unless `entryTypeOf` says otherwise. */
  entryTypes: EntryType[];
}

const IMAGES_AND_PDF = 'image/*,application/pdf';

const group = (
  key: EConfiguratorFileGroup,
  entryTypes: EntryType[],
  options: Partial<IConfiguratorFileGroupConfig> = {}
): IConfiguratorFileGroupConfig => ({
  key,
  title: `${T}.groups.${key}.title`,
  tip: `${T}.groups.${key}.tip`,
  accept: IMAGES_AND_PDF,
  camera: false,
  skippable: true,
  entryTypes,
  ...options
});

/**
 * Upload groups. Files are project entries (`data-box-2`) of the types the project dashboard already shows,
 * so the team finds them where they look for documents (PLAN, D5).
 */
export const CONFIGURATOR_FILE_GROUPS: Record<EConfiguratorFileGroup, IConfiguratorFileGroupConfig> = {
  [EConfiguratorFileGroup.EXISTING_PLANS]: group(EConfiguratorFileGroup.EXISTING_PLANS, [
    EntryType.TYPE_ESTATE_PLAN
  ]),
  [EConfiguratorFileGroup.DOCUMENTS]: group(EConfiguratorFileGroup.DOCUMENTS, [
    EntryType.TYPE_OTHER_DOCUMENTS
  ]),
  [EConfiguratorFileGroup.PICTURES]: group(
    EConfiguratorFileGroup.PICTURES,
    [EntryType.TYPE_ESTATE_PHOTOS, EntryType.TYPE_ESTATE_VIDEOS],
    { accept: 'image/*,video/*', camera: true }
  ),
  [EConfiguratorFileGroup.TARGET_PLANS]: group(EConfiguratorFileGroup.TARGET_PLANS, [
    EntryType.TYPE_EXECUTIVE_PLAN
  ]),
  [EConfiguratorFileGroup.INSPIRATION]: group(
    EConfiguratorFileGroup.INSPIRATION,
    [EntryType.TYPE_INSPIRATIONS],
    {
      accept: 'image/*',
      camera: true,
      skippable: false
    }
  )
};

/** Groups of the photos step, in the order of the prototype. */
export const PHOTO_STEP_FILE_GROUPS = [
  EConfiguratorFileGroup.EXISTING_PLANS,
  EConfiguratorFileGroup.DOCUMENTS,
  EConfiguratorFileGroup.PICTURES,
  EConfiguratorFileGroup.TARGET_PLANS
];

/** Limits of the project documents (ProjectBoxEntryConfig / ProjectVideoBoxEntryConfig), in MB. */
export const MAX_FILE_SIZE_MB = 50;
export const MAX_VIDEO_SIZE_MB = 1800;
export const MAX_FILES_PER_ENTRY = 50;

export interface IConfiguratorFileLike {
  name: string;
  type: string;
  size?: number;
}

const getExtension = (name: string): string => (name.split('.').pop() || '').toUpperCase();

export const isVideoFile = (file: IConfiguratorFileLike): boolean =>
  file.type?.startsWith('video/') || Config.VIDEO_EXTENSIONS.includes(getExtension(file.name));

export const isImageFile = (file: IConfiguratorFileLike): boolean => !!file.type?.startsWith('image/');

/** Pictures: photos and videos go to separate entries, as the project dashboard lists them apart. */
export const getEntryTypeOf = (groupKey: EConfiguratorFileGroup, file: IConfiguratorFileLike): EntryType => {
  const { entryTypes } = CONFIGURATOR_FILE_GROUPS[groupKey];
  return groupKey === EConfiguratorFileGroup.PICTURES && isVideoFile(file) ? entryTypes[1] : entryTypes[0];
};

/** Whether `file` matches the `accept` list of the group (`image/*`, `application/pdf`, ...). */
export const isAcceptedFile = (groupKey: EConfiguratorFileGroup, file: IConfiguratorFileLike): boolean =>
  CONFIGURATOR_FILE_GROUPS[groupKey].accept.split(',').some((accepted) => {
    if (accepted === 'video/*') return isVideoFile(file);
    if (accepted.endsWith('/*')) return !!file.type?.startsWith(accepted.slice(0, -1));
    return file.type === accepted;
  });

export const isTooLarge = (file: IConfiguratorFileLike): boolean =>
  (file.size || 0) > (isVideoFile(file) ? MAX_VIDEO_SIZE_MB : MAX_FILE_SIZE_MB) * 1024 * 1024;

/** What the pictures should show, from the answers: translation keys under `Files.picturesTip`. */
export const getPicturesTipParts = (answers: IConfiguratorAnswers): string[] => {
  const parts: string[] = [];
  if (isCompleteRenovation(answers)) parts.push('rooms');
  if (hasBathroomWork(answers)) parts.push('bathroom');
  if (hasKitchen(answers)) parts.push('kitchen');
  if (isScope(EProjectScope.SINGLE_WORKS)(answers)) parts.push('area');
  if (
    isScope(EProjectScope.EXTERIOR)(answers) ||
    (answers.exteriorWorks || []).some((work) => work !== EXTERIOR_WORK_NONE)
  ) {
    parts.push('exterior');
  }
  return parts.map((part) => `${T}.picturesTip.${part}`);
};

/** Files counted on the photos step (inspiration belongs to step 4). */
export const countPhotoStepFiles = (answers: IConfiguratorAnswers): number =>
  PHOTO_STEP_FILE_GROUPS.reduce((count, key) => count + (answers.fileCounts?.[key] || 0), 0);

/** "Done" for the progress: files uploaded or "Habe ich nicht". */
export const isFileGroupDone = (answers: IConfiguratorAnswers, key: EConfiguratorFileGroup): boolean =>
  (answers.fileCounts?.[key] || 0) > 0 || !!answers.filesSkipped?.includes(key);
